import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ChatMessage from '@/pages/dashboard/apps/mon-assistant/components/ChatMessage';
import ChatVoiceNote from '@/pages/client/dashboard/components/ChatVoiceNote';
import type { AssistantDocument } from '@/pages/dashboard/apps/mon-assistant/types';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import { useMerchantVoice } from '@/hooks/useMerchantVoice';
import { invokeAssistant, loadAssistantHistory, type StoredMessage } from '@/lib/assistantChat';
import { uploadVoiceNote } from '@/lib/voiceStorage';
import type { VoiceResult } from '@/hooks/useVoiceDictation';

interface DockMessage {
  role: 'user' | 'assistant';
  content: string;
  invoice?: AssistantDocument;
  /** Note vocale envoyée (réécoutable comme dans un chat classique). */
  audio?: { url: string; duration: number };
}

const WELCOME =
  "Bonjour ! Je suis votre assistant Zifek. Je connais vos ventes, vos clients, vos produits et vos applications. Posez-moi une question, dictez-la au micro, ou demandez-moi de rédiger et publier un article de blog.";

interface DockConversation {
  key: string;
  label: string;
  count: number;
  messages: StoredMessage[];
}

/** Regroupe l'historique en conversations (une par jour), la plus récente d'abord. */
function groupConversations(history: StoredMessage[]): DockConversation[] {
  const byDay = new Map<string, StoredMessage[]>();
  history.forEach((m) => {
    const day = (m.date || '').slice(0, 10) || 'inconnu';
    const list = byDay.get(day) || [];
    list.push(m);
    byDay.set(day, list);
  });
  return [...byDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([day, list]) => {
      const firstQuestion = list.find((m) => m.role === 'user')?.content || 'Conversation';
      return {
        key: day,
        label: firstQuestion.length > 62 ? `${firstQuestion.slice(0, 62)}…` : firstQuestion,
        count: list.length,
        messages: list,
      };
    });
}

const QUICK_QUESTIONS = [
  'Comment se portent mes ventes ?',
  'Quels produits dois-je réapprovisionner ?',
  'Quels clients me doivent de l’argent ?',
];

/** Détecte une action serveur (facture, devis, comparatif). */
function resolveActionBody(text: string, userId: number): Record<string, unknown> {
  const lower = text.toLowerCase();
  const quoteIntent = /devis/.test(lower)
    && /(cr[eé]e|cr[eé]er|g[eé]n[eè]re|fais|faire|[eé]tablis|envoie|envoyer|pr[eé]pare|[eé]mets|propose)/.test(lower);
  const invoiceIntent = /(facture|facturer)/.test(lower)
    && /(cr[eé]e|cr[eé]er|g[eé]n[eè]re|fais|faire|[eé]tablis|envoie|envoyer|pr[eé]pare|[eé]mets|facter)/.test(lower);
  const comparisonIntent = /(compar|comparatif|semaine pass|semaine derni|par rapport [aà] (la )?semaine)/i.test(text);

  if (quoteIntent) return { action: 'create_document', docType: 'quote', message: text, userId };
  if (invoiceIntent) return { action: 'create_invoice', message: text, userId };
  if (comparisonIntent) return { action: 'sales_comparison', userId };
  return { message: text, userId };
}

/**
 * Assistant commerce flottant.
 * Disponible sur toutes les pages du dashboard (sauf la page assistant
 * complète) : bulle en bas à droite, chat rapide, **note vocale** (le texte
 * part à l'IA, l'audio reste réécoutable), et actions réelles (résumé hebdo,
 * facture / devis, comparatif, rédaction + publication d'un article).
 * Les conversations sont enregistrées et rechargées après rafraîchissement.
 */
export default function AssistantDock() {
  const { user } = useAuth();
  const merchantVoice = useMerchantVoice();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Badge « Nouvelle réponse » : apparaît quand l'assistant répond alors que
  // le panneau est fermé.
  const [unread, setUnread] = useState(false);
  const [messages, setMessages] = useState<DockMessage[]>([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [conversations, setConversations] = useState<DockConversation[]>([]);
  const [blogOpen, setBlogOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [publishing, setPublishing] = useState(false);
  const historyLoadedRef = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, open]);

  // 🔔 Signale une réponse non lue sur la bulle flottante.
  useEffect(() => {
    if (open) {
      setUnread(false);
      return;
    }
    const last = messages[messages.length - 1];
    if (last?.role === 'assistant' && messages.length > 1) setUnread(true);
  }, [messages, open]);

  // ── Restauration de l'historique (une seule fois) ──
  useEffect(() => {
    if (!open || !user?.id || historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    void (async () => {
      const history = await loadAssistantHistory(user.id, 200);
      if (history.length === 0) return;
      setConversations(groupConversations(history));
      setMessages((prev) => [
        ...prev,
        ...history.slice(-60).map((m) => ({ role: m.role, content: m.content, audio: m.audio })),
      ]);
    })();
  }, [open, user?.id]);

  /** Envoie un message (texte libre ou transcription vocale). */
  const send = useCallback(async (
    text: string,
    voice?: { url: string; duration: number; blob?: Blob; mime?: string },
  ) => {
    const trimmed = text.trim();
    if (!trimmed || !user?.id || loading) return;

    // 🎙️ URL persistante de la note vocale (réécoutable après rechargement).
    let audio: { url: string; duration: number } | undefined = voice
      ? { url: voice.url, duration: voice.duration }
      : undefined;
    if (voice?.blob) {
      try {
        const persisted = await uploadVoiceNote(voice.blob, user.id, voice.mime);
        if (persisted) audio = { url: persisted, duration: voice.duration };
      } catch { /* on garde l'URL locale */ }
    }

    setMessages((prev) => [...prev, { role: 'user', content: trimmed, audio }]);
    setInput('');
    setError(null);
    setLoading(true);
    try {
      const body = resolveActionBody(trimmed, user.id);
      const payload = await invokeAssistant(audio ? { ...body, audio } : body);
      setMessages((prev) => [...prev, { role: 'assistant', content: payload.reply, invoice: payload.invoice }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [user, loading]);

  const ask = useCallback(() => { void send(input); }, [send, input]);

  /** Repart d'une conversation vierge. */
  const startNewConversation = useCallback(() => {
    setMessages([{ role: 'assistant', content: WELCOME }]);
    setError(null);
    setView('chat');
  }, []);

  /** Reprend une conversation enregistrée (chargée dans le fil). */
  const openConversation = useCallback((conv: DockConversation) => {
    setMessages([
      { role: 'assistant', content: WELCOME },
      ...conv.messages.map((m) => ({ role: m.role, content: m.content, audio: m.audio })),
    ]);
    setError(null);
    setView('chat');
  }, []);

  // ── Note vocale : le TEXTE part directement à l'IA, l'audio s'affiche ──
  const handleVoiceNote = useCallback((result: VoiceResult) => {
    void send(result.text, {
      url: result.audioUrl,
      duration: result.durationSeconds,
      blob: result.audioBlob,
      mime: result.mimeType,
    });
  }, [send]);

  const weekly = useCallback(async () => {
    if (!user?.id || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: 'Résumé de la semaine (tops + alertes)' }]);
    setLoading(true);
    setError(null);
    try {
      const payload = await invokeAssistant({ weeklySummary: true, userId: user.id });
      setMessages((prev) => [...prev, { role: 'assistant', content: payload.reply }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  }, [user, loading]);

  const publishArticle = useCallback(async () => {
    if (!user?.id || publishing) return;
    const subject = topic.trim();
    if (!subject) return;
    setPublishing(true);
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: `Rédige et publie un article de blog sur : ${subject}` }]);
    try {
      const payload = await invokeAssistant({ action: 'blog_article', topic: subject, publish: true, userId: user.id });
      setMessages((prev) => [...prev, { role: 'assistant', content: payload.reply }]);
      setTopic('');
      setBlogOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La publication a échoué');
    } finally {
      setPublishing(false);
    }
  }, [user, topic, publishing]);

  if (!user) return null;
  // La page complète de l'assistant gère déjà le chat : pas de doublon.
  if (location.pathname.startsWith('/dashboard/mon-assistant')) return null;

  return (
    <>
      {/* Bulle flottante */}
      {!open && (
        <button
          type="button"
          onClick={() => { setUnread(false); setOpen(true); }}
          className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-40 w-14 h-14 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shadow-lg hover:bg-primary-600 transition-colors cursor-pointer"
          title={unread ? 'Nouvelle réponse de l’assistant' : 'Ouvrir mon assistant'}
          aria-label={unread ? 'Nouvelle réponse de l’assistant' : 'Ouvrir mon assistant'}
        >
          <i className="ri-robot-2-line text-2xl"></i>
          {unread ? (
            <span className="absolute -top-1.5 -right-1.5 flex items-center gap-1 pl-1 pr-2 h-6 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold border-2 border-background-50 whitespace-nowrap">
              <i className="ri-notification-3-fill text-[11px]"></i>
              Nouvelle réponse
            </span>
          ) : (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></span>
          )}
        </button>
      )}

      {/* Panneau */}
      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 z-40 w-full sm:w-[400px] h-[85dvh] sm:h-[560px] bg-background-50 border border-background-200/70 rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-xl">
          {/* En-tête */}
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-background-200/70 flex-shrink-0">
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-robot-2-line text-base"></i>
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-background-50"></span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold font-heading text-foreground-950 leading-tight truncate">Mon Assistant</p>
              <p className="text-[10px] text-foreground-500 truncate">
                {loading ? 'en train d’écrire…' : 'Connecté à votre commerce'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setView((v) => (v === 'chat' ? 'history' : 'chat'))}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                view === 'history' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500 hover:bg-background-100'
              }`}
              title="Mes conversations"
              aria-label="Mes conversations"
            >
              <i className="ri-history-line text-sm"></i>
            </button>
            <Link
              to="/dashboard/mon-assistant"
              className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
              title="Ouvrir en plein écran"
            >
              <i className="ri-expand-diagonal-line text-sm"></i>
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
              title="Fermer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
            {view === 'history' ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={startNewConversation}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Nouvelle conversation
                </button>
                {conversations.length === 0 && (
                  <p className="text-[11px] text-foreground-500 text-center py-6">
                    Aucune conversation enregistrée pour le moment.
                  </p>
                )}
                {conversations.map((conv) => (
                  <button
                    key={conv.key}
                    type="button"
                    onClick={() => openConversation(conv)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
                      <i className="ri-calendar-2-line"></i>
                      {conv.key === 'inconnu' ? 'Date inconnue' : new Date(conv.key).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      <span className="ml-auto text-foreground-400">{conv.count} message{conv.count > 1 ? 's' : ''}</span>
                    </span>
                    <span className="mt-1 block text-xs font-medium text-foreground-800 line-clamp-2">{conv.label}</span>
                  </button>
                ))}
              </div>
            ) : messages.map((m, i) => (
              m.audio ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2.5 rounded-2xl rounded-tr-md bg-accent-500 text-background-50">
                    <ChatVoiceNote
                      src={m.audio.url}
                      durationSeconds={m.audio.duration}
                      transcript={m.content}
                    />
                  </div>
                </div>
              ) : (
                <ChatMessage key={i} role={m.role} content={m.content} lang="fr" voice={merchantVoice} invoice={m.invoice} />
              )
            ))}

            {loading && (
              <div className="flex items-center gap-2 pl-10">
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-1.5 pl-10">
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => void send(q)}
                    className="px-2.5 py-1.5 rounded-full border border-background-200/70 text-[11px] text-foreground-600 hover:bg-background-100 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-red-50 text-[11px] text-red-600">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span className="flex-1 break-words">{error}</span>
                <button type="button" onClick={() => setError(null)} className="cursor-pointer flex-shrink-0">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Actions rapides */}
          <div className="px-3 pt-2 flex flex-wrap gap-1.5 flex-shrink-0 border-t border-background-200/70">
            <button
              type="button"
              onClick={weekly}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-background-100 text-[11px] font-medium text-foreground-700 hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-primary-600"></i>
              Résumé hebdo
            </button>
            <button
              type="button"
              onClick={() => setBlogOpen((v) => !v)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                blogOpen ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
              }`}
            >
              <i className="ri-quill-pen-ai-line"></i>
              Rédiger un article
            </button>
            <Link
              to="/dashboard/content-studio/voice"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-background-100 text-[11px] font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <i className="ri-volume-up-line text-accent-600"></i>
              Text-to-Voice
            </Link>
          </div>

          {/* Formulaire article */}
          {blogOpen && (
            <div className="px-3 py-2.5 flex-shrink-0 bg-accent-50/60 border-t border-accent-100">
              <label className="block text-[11px] font-semibold text-foreground-700 mb-1">
                Sujet de l&apos;article (publié directement sur /blog)
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void publishArticle(); }}
                  placeholder="Ex : 5 astuces pour fidéliser ses clients"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
                />
                <button
                  type="button"
                  onClick={publishArticle}
                  disabled={!topic.trim() || publishing}
                  className="flex items-center gap-1 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {publishing ? <><i className="ri-loader-4-line animate-spin"></i>Rédaction…</> : <><i className="ri-send-plane-fill"></i>Publier</>}
                </button>
              </div>
            </div>
          )}

          {/* Saisie */}
          <div className="px-3 py-2.5 flex-shrink-0 border-t border-background-200/70">
            <div className="flex items-center gap-1.5">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                placeholder="Posez votre question ou dictez-la…"
                className="flex-1 min-w-0 px-3.5 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
              <VoiceInputButton userId={user.id} onVoiceResult={handleVoiceNote} />
              <button
                type="button"
                onClick={ask}
                disabled={!input.trim() || loading}
                className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
                title="Envoyer"
              >
                <i className="ri-send-plane-fill text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}